import type {
  HealthcareService,
  Location,
  Organization,
  Practitioner,
  PractitionerRole,
  Reference,
  Schedule,
} from "@medplum/fhirtypes";

import type {
  PractitionerDeactivationWrites,
  PractitionerLinkWrites,
  PractitionerLocationsChange,
  PractitionerServicesChange,
  SchedulePatch,
} from "@/types/clinic-configuration";
import {
  FHIR_EXTENSIONS,
  FHIR_IDENTIFIER_SYSTEMS,
} from "@/constants/fhir.constants";
import { buildVersionedPatchOperations } from "@/lib/utils/clinic-configuration/resource-patch";
import { getExtensionValue } from "@/lib/utils/fhir";
import { getIdentifierValues, getReferenceId } from "@/lib/utils/helpers/fhir";

import { getPractitionerRoles, toServiceId } from "./service-practitioners";

/**
 * A link card's selection as a change against its baseline, so a save
 * writes only what the user changed.
 */
export const toPractitionerLinkChange = (
  baselineIds: string[],
  selectedIds: string[]
): PractitionerLocationsChange & PractitionerServicesChange => {
  const baseline = new Set(baselineIds);
  const selected = new Set(selectedIds);

  return {
    checkedIds: selectedIds.filter((id) => !baseline.has(id)),
    uncheckedIds: baselineIds.filter((id) => !selected.has(id)),
  };
};

/**
 * The services the practitioner offers: every service any of their roles
 * lists, active or not. AKB keeps the same list on all of them.
 */
export const getPractitionerServiceIds = (
  roles: PractitionerRole[],
  practitionerId: string
): string[] => [
  ...new Set(
    getPractitionerRoles(roles, practitionerId).flatMap((role) =>
      (role.healthcareService ?? []).flatMap((reference) => {
        const serviceId = toServiceId(reference);
        return serviceId ? [serviceId] : [];
      })
    )
  ),
];

/**
 * Whether unchecking the service on this practitioner opens it to any
 * practitioner: they are the only practitioner whose roles list it. Inactive
 * practitioners count, since their roles keep the service restricted. Takes
 * `groupServicePractitioners`' result, so a card groups the roles once.
 */
export const isLastServicePractitioner = (
  servicePractitioners: Record<string, string[]>,
  serviceId: string,
  practitionerId: string
): boolean => {
  const practitionerIds = servicePractitioners[serviceId] ?? [];
  return practitionerIds.length === 1 && practitionerIds[0] === practitionerId;
};

type BuildRoleParams = {
  projectId: string;
  practitionerId: string;
  /**
   * The practitioner's AKB ID, `DOC###`.
   */
  practitionerCode: string;
  location: Location;
  /**
   * The clinic details `Organization`.
   */
  clinic: Organization;
  specialty?: PractitionerRole["specialty"];
  serviceIds: string[];
};

/**
 * A new role, the shape AKB writes: one per practitioner and location,
 * with the `<Practitioner ID>:<Location ID>` identifier. The references are
 * literal, with the names as `display`, as AKB's read back. The timezone is
 * the location's, else the clinic's; the language is the clinic's. Neither
 * is written when there is none, and no `specialty` unless one is given:
 * FHIR has no empty values.
 */
export const buildPractitionerRole = ({
  projectId,
  practitionerId,
  practitionerCode,
  location,
  clinic,
  specialty,
  serviceIds,
}: BuildRoleParams): PractitionerRole => {
  const [locationCode] = getIdentifierValues(
    [location],
    FHIR_IDENTIFIER_SYSTEMS.LOCATION
  );

  if (!locationCode) {
    throw new Error(`Location "${location.id}" has no location ID.`);
  }

  const timezone =
    getExtensionValue<string>(location, FHIR_EXTENSIONS.TIMEZONE) ??
    getExtensionValue<string>(clinic, FHIR_EXTENSIONS.TIMEZONE);

  return {
    resourceType: "PractitionerRole",
    meta: { project: projectId },
    active: true,
    ...(clinic.language && { language: clinic.language }),
    identifier: [
      {
        system: FHIR_IDENTIFIER_SYSTEMS.PRACTITIONER_ROLE,
        value: `${practitionerCode}:${locationCode}`,
      },
    ],
    practitioner: { reference: `Practitioner/${practitionerId}` },
    organization: {
      reference: `Organization/${clinic.id}`,
      ...(clinic.name && { display: clinic.name }),
    },
    location: [
      {
        reference: `Location/${location.id}`,
        ...(location.name && { display: location.name }),
      },
    ],
    ...(specialty?.length && { specialty }),
    ...(serviceIds.length > 0 && {
      healthcareService: serviceIds.map((serviceId) => ({
        reference: `HealthcareService/${serviceId}`,
      })),
    }),
    ...(timezone && {
      extension: [{ url: FHIR_EXTENSIONS.TIMEZONE, valueCode: timezone }],
    }),
  };
};

/**
 * The practitioner's stored services with the change applied: checked ones
 * added last, unchecked ones removed.
 */
const applyServicesChange = (
  serviceIds: string[],
  { checkedIds, uncheckedIds }: PractitionerServicesChange
): string[] => {
  const unchecked = new Set(uncheckedIds);
  return [
    ...new Set(
      [...serviceIds, ...checkedIds].filter((id) => !unchecked.has(id))
    ),
  ];
};

/**
 * `role` listing exactly `serviceIds`: stored references that stay are
 * kept as written and in order, new ones go last. FHIR has no empty
 * arrays, so an empty list removes the key.
 */
const setRoleServices = (
  role: PractitionerRole,
  serviceIds: string[]
): PractitionerRole => {
  const wanted = new Set(serviceIds);
  const kept = (role.healthcareService ?? []).filter((reference) => {
    const serviceId = toServiceId(reference);
    return serviceId !== undefined && wanted.has(serviceId);
  });
  const keptIds = new Set(kept.map(toServiceId));
  const healthcareService: Reference<HealthcareService>[] = [
    ...kept,
    ...serviceIds
      .filter((serviceId) => !keptIds.has(serviceId))
      .map((serviceId) => ({ reference: `HealthcareService/${serviceId}` })),
  ];
  const copy: PractitionerRole = { ...role, healthcareService };

  if (healthcareService.length === 0) {
    delete copy.healthcareService;
  }

  return copy;
};

/**
 * Every location the role lists, as `getPractitionerLocationIds` reads
 * them. AKB writes one.
 */
const toRoleLocationIds = (role: PractitionerRole): string[] =>
  (role.location ?? []).flatMap(({ reference }) => {
    const locationId = getReferenceId(reference, "Location");
    return locationId ? [locationId] : [];
  });

/**
 * Whether the save sets the role active, inactive, or leaves it: checking
 * one of its locations finds an inactive role (an active one would already
 * be checked), unchecking one finds an active one.
 */
const toWantedActive = (
  role: PractitionerRole,
  { checkedIds, uncheckedIds }: PractitionerLocationsChange
): boolean | undefined => {
  const locationIds = toRoleLocationIds(role);

  if (
    role.active === false &&
    locationIds.some((id) => checkedIds.includes(id))
  ) {
    return true;
  }
  if (
    role.active !== false &&
    locationIds.some((id) => uncheckedIds.includes(id))
  ) {
    return false;
  }

  return undefined;
};

const requireVersionId = (resource: PractitionerRole | Schedule): string => {
  const versionId = resource.meta?.versionId;

  if (!versionId) {
    throw new Error(
      `${resource.resourceType} "${resource.id}" has no versionId.`
    );
  }

  return versionId;
};

type LinkWritesParams = {
  projectId: string;
  practitioner: Practitioner;
  /**
   * The clinic's roles, `Schedule`s and locations the cards show, so each
   * patch tests the version the user saw.
   */
  roles: PractitionerRole[];
  schedules: Schedule[];
  locations: Location[];
  /**
   * The clinic details `Organization`. Needed only for a new role.
   */
  clinic: Organization | undefined;
  locationsChange?: PractitionerLocationsChange;
  servicesChange?: PractitionerServicesChange;
};

/**
 * What a Practitioner save writes to the practitioner's links. Each existing
 * role gets at most one patch, with its `active` and its services together: a
 * second patch of the same role in one transaction would fail its version
 * test.
 *
 * Locations, for each one the user changed:
 * - checked, no role there: a new role (`buildPractitionerRole`), with the
 *   specialty of the practitioner's first role and the services after this
 *   save;
 * - checked, an inactive role: the role set active;
 * - unchecked: the active role set inactive. It is kept, so it keeps its
 *   services and past appointments keep a role to point at.
 *
 * A `Schedule` is active while its role is (the practitioner is active, or the
 * drawer would not open), so each `Schedule` whose `actor` is a role this
 * save turns on or off follows it. Booking reads only `Schedule.active`.
 *
 * Services: every role of the practitioner, active or not, gets the same
 * final list, as AKB writes it.
 */
export const buildPractitionerLinkWrites = ({
  projectId,
  practitioner,
  roles,
  schedules,
  locations,
  clinic,
  locationsChange = { checkedIds: [], uncheckedIds: [] },
  servicesChange,
}: LinkWritesParams): PractitionerLinkWrites => {
  const practitionerId = practitioner.id ?? "";
  const practitionerRoles = getPractitionerRoles(roles, practitionerId);
  const serviceIds = servicesChange
    ? applyServicesChange(
        getPractitionerServiceIds(roles, practitionerId),
        servicesChange
      )
    : getPractitionerServiceIds(roles, practitionerId);

  const rolePatches: PractitionerLinkWrites["rolePatches"] = [];
  const schedulePatches: SchedulePatch[] = [];

  for (const role of practitionerRoles) {
    const wantedActive = toWantedActive(role, locationsChange);
    const withServices = servicesChange
      ? setRoleServices(role, serviceIds)
      : role;
    const edited =
      wantedActive === undefined
        ? withServices
        : { ...withServices, active: wantedActive };
    const operations = buildVersionedPatchOperations(
      role,
      edited,
      requireVersionId(role)
    );

    /**
     * Only the version test: nothing changed on this role.
     */
    if (operations.length > 1 && role.id) {
      rolePatches.push({ id: role.id, operations });
    }
    if (wantedActive === undefined) {
      continue;
    }

    for (const schedule of schedules) {
      const isRoleSchedule = (schedule.actor ?? []).some(
        ({ reference }) => reference === `PractitionerRole/${role.id}`
      );

      if (
        isRoleSchedule &&
        schedule.id &&
        (schedule.active !== false) !== wantedActive
      ) {
        schedulePatches.push({
          id: schedule.id,
          operations: buildVersionedPatchOperations(
            schedule,
            { ...schedule, active: wantedActive },
            requireVersionId(schedule)
          ),
        });
      }
    }
  }

  const roleLocationIds = new Set(practitionerRoles.flatMap(toRoleLocationIds));
  const newLocations = locationsChange.checkedIds.flatMap((locationId) => {
    const location = locations.find(({ id }) => id === locationId);
    return location && !roleLocationIds.has(locationId) ? [location] : [];
  });

  if (newLocations.length === 0) {
    return { roleCreates: [], rolePatches, schedulePatches };
  }

  const [practitionerCode] = getIdentifierValues(
    [practitioner],
    FHIR_IDENTIFIER_SYSTEMS.PRACTITIONER
  );

  if (!practitionerCode) {
    throw new Error(
      `Practitioner "${practitionerId}" has no AKB practitioner ID.`
    );
  }
  if (!clinic) {
    throw new Error(
      `Project "${projectId}" has no clinic details Organization.`
    );
  }

  const roleCreates = newLocations.map((location) =>
    buildPractitionerRole({
      projectId,
      practitionerId,
      practitionerCode,
      location,
      clinic,
      specialty: practitionerRoles[0]?.specialty,
      serviceIds,
    })
  );

  return { roleCreates, rolePatches, schedulePatches };
};

type DeactivationWritesParams = {
  practitioner: Practitioner;
  /**
   * The `Practitioner`'s, as the user loaded it.
   */
  versionId: string;
  /**
   * Read fresh, with `schedules`: a role or `Schedule` missing here would
   * leave the practitioner bookable there.
   */
  roles: PractitionerRole[];
  schedules: Schedule[];
};

/**
 * What deactivating a practitioner writes: the `Practitioner` set inactive, and
 * each active `Schedule` whose `actor` is one of the practitioner's roles,
 * since booking reads only `Schedule.active`. Nothing else: the roles keep
 * the practitioner's services, so a service only they offered stays restricted,
 * and a `Schedule` already inactive (its location was unchecked) is left
 * as it is.
 */
export const buildPractitionerDeactivationWrites = ({
  practitioner,
  versionId,
  roles,
  schedules,
}: DeactivationWritesParams): PractitionerDeactivationWrites => {
  const roleReferences = new Set(
    getPractitionerRoles(roles, practitioner.id ?? "").map(
      ({ id }) => `PractitionerRole/${id}`
    )
  );
  const schedulePatches = schedules.flatMap((schedule) => {
    const isPractitionerSchedule = (schedule.actor ?? []).some(
      ({ reference }) => !!reference && roleReferences.has(reference)
    );

    return isPractitionerSchedule && schedule.id && schedule.active !== false
      ? [
          {
            id: schedule.id,
            operations: buildVersionedPatchOperations(
              schedule,
              { ...schedule, active: false },
              requireVersionId(schedule)
            ),
          },
        ]
      : [];
  });

  return {
    practitionerOperations: buildVersionedPatchOperations(
      practitioner,
      { ...practitioner, active: false },
      versionId
    ),
    schedulePatches,
  };
};
